import React, {useContext, useEffect, useState} from 'react';
import {ScrollView, View} from 'react-native';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import FilterButton from '/component/base/FilterButton';
import {NewLabel} from '/component/base/NewLabel';
import Text from '/component/base/Text';
import {ClientRoleComponent} from '/component/partial/report/ClientRoleComponent';
import OrderDate from '/component/partial/report/OrderDate';
import OrdersListHeader from '/component/partial/report/OrdersListHeader';
import OrdersReportDataRenderer from '/component/partial/report/OrdersReportDataRenderer';
import ReportDataProvider from '/component/partial/report/ReportDataProvider';
import {Reports} from '/constant/TestIDs';
import {REPORT_ALL_ORDERS} from '/enum/records.enum';
import {useTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {styles} from './AllOrdersReport.style';
import {AllOrdersContentProps} from './AllOrdersReport.types';
import {FilterOptionProps} from './AllOrdersReport.types';
import {getGenerationItems} from './AllOrdersReport.util';
import {ClearFilter} from './ClearFilter';
import ModalFilter from './ModalPopoverFilter.connect';
import reportPayload from './reportPayload';

import {RecordsModalContext} from '/context/RecordsModal';
import {useRecordsSorting} from '/context/RecordsSort/useRecordsSorting';

const MAX_FONT_SIZE_MULTIPLIER = 2;

export const AllOrdersContent = (data: AllOrdersContentProps) => {
  const {isReadyToLoad, sortParam} = useRecordsSorting({
    recordsName: reportPayload.reportName,
  });
  const {reportHeading = '', recordsShowRecord = () => {}} = data || {};
  const optionSelected = (option: string, radioGroup: string) => {
    let filterOptionList: FilterOptionProps[] = [];
    if (radioGroup === 'generationItems') {
      filterOptionList = generationOptions;
    } else if (radioGroup === 'productItems') {
      filterOptionList = productsOptions;
    } else if (radioGroup === 'accountStatusItems') {
      filterOptionList = accountStatusOptions;
    }
    const statusList =
      filterOptionList?.find(filter => filter.selected === true) || {};
    const statusListNew: FilterOptionProps[] =
      option !== statusList.id
        ? filterOptionList.map(item => {
            const {selected, id} = item || {};
            if (id === statusList.id) {
              item.selected = !statusList.selected;
            }
            if (id === option) {
              item.selected = !selected;
            }
            return item;
          })
        : filterOptionList;

    if (radioGroup === 'generationItems') {
      setGenerationOptions(statusListNew);
    } else if (radioGroup === 'productItems') {
      setProductsOptions(statusListNew);
    } else if (radioGroup === 'accountStatusItems') {
      setAccountStatusOptions(statusListNew);
    }
  };
  const productItems: FilterOptionProps[] = [
    {
      text: 'All (default)',
      id: 'all',
      selected: true,
      onPress: () => {
        optionSelected('all', 'productItems');
      },
      testID: Reports.reportFilterByProductsAll,
      defaultVal: true,
    },
    {
      text: 'Active products',
      id: 'active',
      selected: false,
      onPress: () => {
        optionSelected('active', 'productItems');
      },
      testID: Reports.reportFilterByProductsActive,
      defaultVal: false,
    },
  ];
  const accountStatusItems: FilterOptionProps[] = [
    {
      text: 'All (default)',
      id: 'all',
      selected: true,
      onPress: () => {
        optionSelected('all', 'accountStatusItems');
      },
      testID: Reports.reportFilterByAccountAll,
      defaultVal: true,
    },
    {
      text: 'New (added this month)',
      id: 'new',
      selected: false,
      onPress: () => {
        optionSelected('new', 'accountStatusItems');
      },
      testID: Reports.reportFilterByAccountNew,
      defaultVal: false,
    },
  ];

  const generationItems: FilterOptionProps[] = getGenerationItems({
    reportHeading,
    optionSelected,
  });

  const {tt, ts} = useTranslation('report.allOrders');
  const {setIsModalOpen} = useContext(RecordsModalContext);
  const [modalVisible, setModalVisible] = useState(false);
  const [applyFilter, setApplyFilter] = useState(false);
  const [generationOptions, setGenerationOptions] = useState(generationItems);
  const [generationWithoutFilter, setGenerationWithoutFilter] = useState();
  const [productsOptions, setProductsOptions] = useState(productItems);
  const [productsWithoutFilter, setProductsWithoutFilter] = useState();
  const [accountStatusOptions, setAccountStatusOptions] =
    useState(accountStatusItems);
  const [accountWithoutFilter, setAccountWithoutFilter] = useState();
  /*  This handles the selected and unselected cases based on a boolean value */
  const [filterCount, setfilterCount] = useState(0);

  const filters = ['Generation', 'Product Type', 'Account Status'];
  const [, setActiveIndex] = useState(0);

  const handleNextFilter = () => {
    setActiveIndex(prev => (prev + 1) % filters.length);
  };

  const handlePrevFilter = () => {
    setActiveIndex(prev => (prev - 1 + filters.length) % filters.length);
  };

  useEffect(() => {
    let totalLength = [];
    let generationFilter = generationOptions?.filter(
      filter => filter.id !== 'frontline' && filter.selected === true,
    );
    if (reportHeading === 'Generation 0 Volume') {
      generationFilter = generationOptions?.filter(
        filter => filter.id !== 'genZero' && filter.selected === true,
      );
    } else if (reportHeading === 'Total Generation Volume') {
      generationFilter = generationOptions?.filter(
        filter => filter.id !== 'allOrg' && filter.selected === true,
      );
    }
    const productFilter = productsOptions?.filter(
      filter => filter.id !== 'all' && filter.selected === true,
    );
    const accountStatusFilter = accountStatusOptions?.filter(
      filter => filter.id !== 'all' && filter.selected === true,
    );
    totalLength = [
      ...generationFilter,
      ...productFilter,
      ...accountStatusFilter,
    ];
    setfilterCount(totalLength.length);
  }, [generationOptions, productsOptions, accountStatusOptions, reportHeading]);

  useEffect(() => {
    setIsModalOpen?.(modalVisible);

    return () => {
      setIsModalOpen?.(false);
    };
  }, [modalVisible, setIsModalOpen]);

  const openModal = () => {
    setModalVisible(true);
    setApplyFilter(false);
    const generationFilter = generationOptions?.find(
      generationItem => generationItem.selected === true,
    );
    const productsOptionsFilter = productsOptions?.find(
      productItem => productItem.selected === true,
    );
    const accountStatusOptionsFilter = accountStatusOptions?.find(
      accountStatusItem => accountStatusItem.selected === true,
    );
    /* WithoutFilter: If the user selects a filter and then closes the popover without clicking "Apply Filters",
    and subsequently reopens the popover, we are supposed to display the previously applied filters. */
    // @ts-expect-error TS(2345): Argument of type '{ text: string; id: string; onPr... Remove this comment to see the full error message
    setGenerationWithoutFilter(generationFilter);
    // @ts-expect-error TS(2345): Argument of type '{ text: string; id: string; sele... Remove this comment to see the full error message
    setProductsWithoutFilter(productsOptionsFilter);
    // @ts-expect-error TS(2345): Argument of type '{ text: string; id: string; sele... Remove this comment to see the full error message
    setAccountWithoutFilter(accountStatusOptionsFilter);
  };

  if (!isReadyToLoad) {
    return null;
  }

  const generationFilterValue = generationOptions?.find(
    generationItem => generationItem.selected === true,
  );
  /* This action removes the '(default)' tag from the Active Filters: while displaying*/
  const textInBracketsRegex = / *\(.+\)/;
  const generationString = generationFilterValue?.text;
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const generationFilterValueNewString = generationString
    .replace(textInBracketsRegex, '')
    .trim();

  const productsOptionsFilterValue = productsOptions?.find(
    productItem => productItem.selected === true,
  );
  const productString = productsOptionsFilterValue?.text;
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const productsFilterValueNewString = productString
    .replace(textInBracketsRegex, '')
    .trim();

  const accountStatusOptionsFilterValue = accountStatusOptions?.find(
    accountStatusItem => accountStatusItem.selected === true,
  );
  const accountStatusString = accountStatusOptionsFilterValue?.text;
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const accountStatusFilterValueNewString = accountStatusString
    .replace(textInBracketsRegex, '')
    .trim();
  return (
    <>
      <View {...testProperties(null, true)}>
        <ScrollView
          horizontal={true}
          contentContainerStyle={styles.scrollViewContainer}
          scrollEnabled={true}
          {...testProperties(Reports.reportFiltersScrollView, true)}>
          <View style={styles.filtersButtonContainer}>
            <View style={styles.viewContainer} {...testProperties(null, true)}>
              <FilterButton
                onPress={openModal}
                text={tt('generation', 'Generation')}
                // @ts-expect-error TS(2322): Type '{ onPress: () => void; text: any; style: Vie... Remove this comment to see the full error message
                style={styles.buttonWidth}
                testID={Reports.reportFilterGeneration}
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                active={!generationFilterValue.defaultVal}
                allowFontScaling
                maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
                onNextFilter={handleNextFilter}
                onPreviousFilter={handlePrevFilter}
              />
              <FilterButton
                onPress={openModal}
                text={tt('productType', 'Product Type')}
                // @ts-expect-error TS(2322): Type '{ onPress: () => void; text: any; style: Vie... Remove this comment to see the full error message
                style={styles.buttonWidth}
                testID={Reports.reportFilterProductType}
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                active={!productsOptionsFilterValue.defaultVal}
                allowFontScaling
                maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
                onNextFilter={handleNextFilter}
                onPreviousFilter={handlePrevFilter}
              />
              <FilterButton
                onPress={openModal}
                text={tt('accountStatus', 'Account Status')}
                // @ts-expect-error TS(2322): Type '{ onPress: () => void; text: any; style: Vie... Remove this comment to see the full error message
                style={styles.buttonWidth}
                testID={Reports.reportFilterAccountStatus}
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                active={!accountStatusOptionsFilterValue.defaultVal}
                allowFontScaling
                maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
                onNextFilter={handleNextFilter}
                onPreviousFilter={handlePrevFilter}
              />
            </View>
            <KeyboardFocusable>
              <View
                style={styles.viewContainer}
                {...testProperties(null, true)}
                accessible={true}
                accessibilityLabel={`Active filters: ${generationFilterValueNewString}, ${productsFilterValueNewString}, ${accountStatusFilterValueNewString}`}>
                <Text
                  accessible={true}
                  style={styles.filterText}
                  allowFontScaling
                  maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
                  {...testProperties(Reports.reportActiveFiltersLabel)}
                  isBold>
                  {tt('active filters', 'Active filters: ')}
                </Text>

                <Text
                  style={styles.filterTextVal}
                  allowFontScaling
                  maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
                  {...testProperties(Reports.reportActiveFiltersGenLabel)}
                  accessible={true}>
                  {tt(
                    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                    `generationFilterValue-${generationFilterValue.text}`,
                    generationFilterValueNewString,
                  )}
                  ,
                </Text>
                <Text
                  style={styles.filterTextVal}
                  allowFontScaling
                  maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
                  {...testProperties(Reports.reportActiveFiltersProductLabel)}
                  accessible={true}>
                  {tt(
                    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                    `productsOptionsFilterValue-${productsOptionsFilterValue.text}`,
                    productsFilterValueNewString,
                  )}
                  ,
                </Text>
                <Text
                  style={styles.filterTextVal}
                  allowFontScaling
                  maxFontSizeMultiplier={MAX_FONT_SIZE_MULTIPLIER}
                  {...testProperties(Reports.reportActiveFiltersAccountLabel)}
                  accessible={true}>
                  {tt(
                    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                    `accountStatusOptionsFilterValue-${accountStatusOptionsFilterValue.text}`,
                    accountStatusFilterValueNewString,
                  )}
                </Text>
              </View>
            </KeyboardFocusable>
          </View>
        </ScrollView>

        <ModalFilter
          {...reportPayload}
          setModalVisible={setModalVisible}
          modalVisible={modalVisible}
          reportHeading={reportHeading}
          applyFilter={applyFilter}
          setApplyFilter={setApplyFilter}
          generationOptions={generationOptions}
          setGenerationOptions={setGenerationOptions}
          generationWithoutFilter={generationWithoutFilter}
          productsOptions={productsOptions}
          setProductsOptions={setProductsOptions}
          productsWithoutFilter={productsWithoutFilter}
          accountStatusOptions={accountStatusOptions}
          setAccountStatusOptions={setAccountStatusOptions}
          accountWithoutFilter={accountWithoutFilter}
          filterCount={filterCount}
          reportName={REPORT_ALL_ORDERS}
          setfilterCount={setfilterCount}
          loadingData={data}
          reportParams={[{...sortParam.params}]}
          setGenerationWithoutFilter={setGenerationWithoutFilter}
          setProductsWithoutFilter={setProductsWithoutFilter}
          setAccountWithoutFilter={setAccountWithoutFilter}
        />
      </View>
      <ReportDataProvider
        {...reportPayload}
        filtersData={{
          level: generationWithoutFilter
            ? // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
              generationWithoutFilter.id
            : // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              generationFilterValue.id,
          products: productsWithoutFilter
            ? // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
              productsWithoutFilter.id
            : // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              productsOptionsFilterValue.id,
          accountStatus: accountWithoutFilter
            ? // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
              accountWithoutFilter.id
            : // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              accountStatusOptionsFilterValue.id,
        }}
        dataRenderer={OrdersReportDataRenderer}
        dataRendererProps={{
          listHeaderComponent: OrdersListHeader,
          personRoleComponent: ClientRoleComponent,
          personInfoComponent: OrderDate,
          NewLabelComponent: NewLabel,
          alternateItemKey: 'orderId',
          onRecordPress: recordsShowRecord,
          filtersData: {
            level: generationWithoutFilter
              ? // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
                generationWithoutFilter.id
              : // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                generationFilterValue.id,
            products: productsWithoutFilter
              ? // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
                productsWithoutFilter.id
              : // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                productsOptionsFilterValue.id,
            accountStatus: accountWithoutFilter
              ? // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
                accountWithoutFilter.id
              : // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                accountStatusOptionsFilterValue.id,
          },
        }}
        loadingData={data}
        reportParams={[{...sortParam.params}]}
      />
      {filterCount > 0 && (
        <ClearFilter
          {...reportPayload}
          setGenerationOptions={setGenerationOptions}
          setProductsOptions={setProductsOptions}
          setAccountStatusOptions={setAccountStatusOptions}
          generationItems={generationItems}
          productItems={productItems}
          accountStatusItems={accountStatusItems}
          text={ts('clearFilter', `Clear filters (${filterCount})`)}
          setGenerationWithoutFilter={setGenerationWithoutFilter}
          setProductsWithoutFilter={setProductsWithoutFilter}
          setAccountWithoutFilter={setAccountWithoutFilter}
        />
      )}
    </>
  );
};
